/**
 * The social marks the platform ships (ported from terraboma, 2026-10-16).
 *
 * The SVGs live in `public/social/<slug>.svg` and are authored `fill=currentColor`
 * so a footer tints them rather than pasting brand colour onto a dark page. This
 * table is what the admin picker offers and what the footer resolves a stored
 * `icon` value against — one list, so an operator can never pick an icon that has
 * no file (`social-icons.test.ts` fails if the two ever drift). The static pages
 * (guide/news/article) use a plain-JS twin, `public/social-links.js`; the same
 * test holds its slug list to this one.
 *
 * `brand` is only used to preview a mark in the picker in its own colour; nothing
 * renders it on the site.
 */
export interface SocialIcon {
  /** Matches the filename: `public/social/<slug>.svg`. */
  slug: string;
  label: string;
  brand: string;
}

export const SOCIAL_ICONS: SocialIcon[] = [
  { slug: 'discord', label: 'Discord', brand: '#5865F2' },
  { slug: 'x', label: 'X (Twitter)', brand: '#000000' },
  { slug: 'youtube', label: 'YouTube', brand: '#FF0000' },
  { slug: 'twitch', label: 'Twitch', brand: '#9146FF' },
  { slug: 'instagram', label: 'Instagram', brand: '#E4405F' },
  { slug: 'facebook', label: 'Facebook', brand: '#1877F2' },
  { slug: 'tiktok', label: 'TikTok', brand: '#010101' },
  { slug: 'reddit', label: 'Reddit', brand: '#FF4500' },
  { slug: 'telegram', label: 'Telegram', brand: '#26A5E4' },
  { slug: 'whatsapp', label: 'WhatsApp', brand: '#25D366' },
  { slug: 'steam', label: 'Steam', brand: '#171A21' },
  { slug: 'linkedin', label: 'LinkedIn', brand: '#0A66C2' },
  { slug: 'vk', label: 'VK', brand: '#0077FF' },
  { slug: 'patreon', label: 'Patreon', brand: '#FF424D' },
  { slug: 'threads', label: 'Threads', brand: '#000000' },
  { slug: 'bluesky', label: 'Bluesky', brand: '#0285FF' },
  { slug: 'mastodon', label: 'Mastodon', brand: '#6364FF' },
];

/**
 * Inline style that paints a mark in the CURRENT TEXT COLOUR.
 *
 * An SVG loaded through `<img>` is an isolated document: its `currentColor`
 * resolves against nothing and paints black, which on this footer is a black mark
 * on a dark ground — invisible. A CSS mask uses the file as a stencil and fills it
 * with `currentColor` from the element, so one file serves the dark footer, the
 * light admin tile and a hover colour without a second copy.
 */
export function socialMaskStyle(src: string): string {
  const url = `url("${src}")`;
  return `background-color:currentColor;`
    + `-webkit-mask:${url} center/contain no-repeat;mask:${url} center/contain no-repeat;`;
}

/** URL of a mark's SVG, or null when the stored icon is unknown or empty. */
export function socialIconSrc(icon: string | null | undefined): string | null {
  if (!icon) return null;
  const slug = icon.trim().toLowerCase();
  return SOCIAL_ICONS.some((i) => i.slug === slug) ? `/social/${slug}.svg` : null;
}

/**
 * The icon to show for a link, falling back to the PLATFORM name when no icon
 * was chosen — an operator who typed "discord" as the platform and left the icon
 * empty still gets the Discord mark, which is what they meant.
 */
export function resolveSocialIcon(link: { icon?: string | null; platform?: string | null }): string | null {
  return socialIconSrc(link.icon) ?? socialIconSrc(link.platform);
}
